import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useAsyncInterval } from '../../utils/hooks';
import { postDocAssistantCapture, getDocAssistantCaptureStatus, postDocAssistantAssign } from '../../../api/emailsApi/docAssistant';
import { setDocAssistantCaptured, setIsSearchingRelatedRecords } from '../../actions/mail';
import { applyDocAssistantFileResults, clearDocAssistantProcessing } from '../../actions/files';
import { refreshSavedLocations } from '../../utils/savedLocations';
import { logError } from '../../utils/request';
import { CAPTURE_STATUS } from './DocAssistantCaptureContext';

const POLL_INTERVAL_MS = 10000;
// Poll on a slight backoff (up to the cap) so a long-running pipeline is not hammered.
const POLL_MAX_INTERVAL_MS = 30000;
const POLL_BACKOFF_STEP_MS = 5000;
// Classification + matter-matching can run for several minutes; keep waiting up to 15 minutes rather
// than concluding "no matter" early.
const MAX_POLL_DURATION_MS = 15 * 60 * 1000;
// The rename step runs AFTER a file is already terminal, so keep polling a few ticks past 'complete'
// to pick up the new names before stopping.
const RENAME_GRACE_TICKS = 5;
// An Automatic capture may not have started when the pane opens; keep checking this long for one.
const CAPTURE_WAIT_MS = 3 * 60 * 1000;
// Automatic only captures newly arrived mail, so only a recent email is worth waiting on.
const RECENT_EMAIL_MS = 10 * 60 * 1000;
// Doc Assistant can file a document after the pipeline reports it complete, so keep refreshing after
// assign until every tracked file reflects as saved, within this budget.
const SETTLE_INTERVAL_MS = 15000;
const SETTLE_MAX_TICKS = 8;
// Capture status the backend reports for a capture that finished with nothing to show.
const BACKEND_SKIPPED = 'skipped';
const NO_MATTER_MESSAGE = 'No matter assigned. Manually select and add at least one saved location.';
const CAPTURE_FAILED_MESSAGE = 'Assign with ACE failed. Try again or manually add a saved location.';

// Owns the Doc Assistant capture poll loop and view transitions. Mounted once in SaveToContainer so the
// poll survives the search <-> selected-record navigation a match triggers; the button and search
// skeleton read the returned state through DocAssistantCaptureContext.
export const useDocAssistantCapture = () => {
  const dispatch = useDispatch();
  const store = useStore();

  const enabled = useSelector((state) => state.mail.settings?.docAssistant?.enabled);
  const automatic = useSelector((state) => state.mail.settings?.docAssistant?.automatic);
  const emailId = useSelector((state) => state.mail.activeEmail);
  const emailCreatedAt = useSelector((state) => state.mail.emails?.dateTimeCreated);
  const ownerEmail = useSelector((state) => state.mail.ownerEmail);
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);
  const docrioNamespace = useSelector((state) => state.mail.settings?.override?.docrioNamespace || 'litify_docs');

  const [status, setStatus] = useState(CAPTURE_STATUS.IDLE);
  const [banner, setBanner] = useState(null);
  // The poll delay grows over the wait (backoff); pollDeadline is the wall-clock give-up time.
  const [pollDelay, setPollDelay] = useState(POLL_INTERVAL_MS);
  const pollDeadline = useRef(0);
  // Extra poll ticks allowed after 'complete' so the rename step's new names can land.
  const postCompleteTicks = useRef(0);
  // Set once the first match navigates the pane to the documents view.
  const matchedRef = useRef(false);
  // Guards the async finalize (assign or no-matter) so a later poll tick cannot re-run it.
  const finalizingRef = useRef(false);
  // useAsyncInterval keeps firing while a slow status request is pending; overlapping ticks are skipped.
  const pollInFlightRef = useRef(false);
  const waitDeadline = useRef(0);
  const settleTicks = useRef(0);
  // Content hashes of the captured files that matched a matter, from the latest status response.
  const trackedHashes = useRef([]);
  // A status request can resolve after the user opens another email; its result is then discarded.
  const activeEmailRef = useRef(emailId);
  activeEmailRef.current = emailId;

  const dismissBanner = () => setBanner(null);

  // Read through the store so a request that resolves after the user switched messages is dropped
  // before it can write the old email's data into the new message's state.
  const isStale = (requestedId) => store.getState().mail.activeEmail !== requestedId;

  // Read through the store rather than a selector: the poll interval would otherwise see a stale closure.
  // Files not listed in the pane (e.g. the .eml) can never show as saved, so they are not waited on.
  const trackedFilesSaved = () => {
    const docrioFiles = store.getState().files?.docrioFiles || [];
    return trackedHashes.current.every((hash) => {
      const rows = docrioFiles.filter((file) => file.hash === hash);
      return !rows.length || rows.some((file) => file.saved);
    });
  };

  const beginSearching = () => {
    pollDeadline.current = Date.now() + MAX_POLL_DURATION_MS;
    setPollDelay(POLL_INTERVAL_MS);
    postCompleteTicks.current = 0;
    matchedRef.current = false;
    finalizingRef.current = false;
    setStatus(CAPTURE_STATUS.SEARCHING);
  };

  const finishWithoutMatter = async () => {
    // Capture confirmed but no auto-assigned matter: the .eml is parked, the user picks the record.
    const requestedId = emailId;
    dispatch(setDocAssistantCaptured(true));
    let filedRecords = 0;
    try {
      filedRecords = await refreshSavedLocations(dispatch, { emailId: requestedId, ownerEmail, docrioNamespace, isCancelled: () => isStale(requestedId) });
    } catch (e) {
      logError(e);
    }
    if (isStale(requestedId)) {
      return;
    }
    // Attachments the pipeline filed already show their matter, so the pane leaves search without the banner.
    if (filedRecords) {
      dispatch(setIsSearchingRelatedRecords(false));
      setStatus(CAPTURE_STATUS.IDLE);
      return;
    }
    setBanner(NO_MATTER_MESSAGE);
    setStatus(CAPTURE_STATUS.NO_MATTER);
  };

  const failCapture = () => {
    // Nothing was parked, so the captured flag must stay off or Add would route through assign.
    setBanner(CAPTURE_FAILED_MESSAGE);
    setStatus(CAPTURE_STATUS.IDLE);
  };

  const stopPolling = () => {
    dispatch(clearDocAssistantProcessing());
    setStatus(CAPTURE_STATUS.IDLE);
  };

  // Matched files are already reparented, so getEmail reflects them before assign runs. Leave the search
  // view for the documents view; the remaining files keep rendering as skeletons there until the poll
  // finishes.
  const showMatched = async () => {
    const requestedId = emailId;
    try {
      await refreshSavedLocations(dispatch, { emailId: requestedId, ownerEmail, docrioNamespace, isCancelled: () => isStale(requestedId) });
    } catch (e) {
      logError(e);
    }
    if (isStale(requestedId)) {
      return;
    }
    dispatch(setIsSearchingRelatedRecords(false));
    setStatus(CAPTURE_STATUS.MATCHED);
  };

  // The backend reports only the .eml's own match; attachment matches never pick the email's record,
  // so a capture with only attachment matches falls through to the user picking a record.
  const finishWithMatters = async (relatedTos) => {
    const requestedId = emailId;
    try {
      // Doc Assistant already filed the .eml; assign only writes the timeline/thread bookkeeping
      // (filesAlreadyAssigned skips the reparent) so the matched record shows as a saved location.
      await postDocAssistantAssign({ externalId: requestedId, relatedTos, filesAlreadyAssigned: true });
      if (isStale(requestedId)) {
        return;
      }
      // Pull the final reflection so every matched file (and its reparented name) is saved.
      await refreshSavedLocations(dispatch, { emailId: requestedId, ownerEmail, docrioNamespace, isCancelled: () => isStale(requestedId) });
      if (isStale(requestedId)) {
        return;
      }
      dispatch(setDocAssistantCaptured(false));
      if (!matchedRef.current) {
        matchedRef.current = true;
        dispatch(setIsSearchingRelatedRecords(false));
      }
      if (trackedFilesSaved()) {
        stopPolling();
      } else {
        settleTicks.current = 0;
        setPollDelay(SETTLE_INTERVAL_MS);
        setStatus(CAPTURE_STATUS.SETTLING);
      }
    } catch (e) {
      logError(e);
      if (isStale(requestedId)) {
        return;
      }
      dispatch(clearDocAssistantProcessing());
      await finishWithoutMatter();
    }
  };

  // An automated capture may already be running when the pane opens; pick up its progress so the
  // searching view shows without the button ever being pressed. With Automatic on, a capture that has
  // not started yet is watched for.
  useEffect(() => {
    setStatus(CAPTURE_STATUS.IDLE);
    trackedHashes.current = [];
    if (!enabled || isComposingEmail || !emailId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getDocAssistantCaptureStatus(emailId);
        if (cancelled) {
          return;
        }
        if (response?.data?.status === 'processing') {
          beginSearching();
        } else if (!response?.data?.status && automatic && isRecentEmail(emailCreatedAt)) {
          waitDeadline.current = Date.now() + CAPTURE_WAIT_MS;
          setPollDelay(POLL_INTERVAL_MS);
          setStatus(CAPTURE_STATUS.WAITING);
        }
      } catch (e) {
        logError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, enabled, automatic, isComposingEmail]);

  const waitForCapture = async () => {
    // Stop once the wait is over or the email already shows saved locations (captured before, or saved
    // by hand), whether or not the status request below succeeds.
    const hasSavedLocations = Object.keys(store.getState().mail?.selectedRecords || {}).length > 0;
    if (Date.now() >= waitDeadline.current || hasSavedLocations) {
      setStatus(CAPTURE_STATUS.IDLE);
      return;
    }
    const requestedId = emailId;
    const response = await getDocAssistantCaptureStatus(requestedId);
    if (activeEmailRef.current !== requestedId) {
      return;
    }
    if (response?.data?.status === BACKEND_SKIPPED) {
      setStatus(CAPTURE_STATUS.IDLE);
    } else if (response?.data?.status) {
      beginSearching();
    }
  };

  const settle = async () => {
    settleTicks.current += 1;
    const requestedId = emailId;
    await refreshSavedLocations(dispatch, { emailId: requestedId, ownerEmail, docrioNamespace, isCancelled: () => isStale(requestedId) });
    if (isStale(requestedId)) {
      return;
    }
    if (trackedFilesSaved() || settleTicks.current >= SETTLE_MAX_TICKS) {
      stopPolling();
    }
  };

  useAsyncInterval(async () => {
    if (pollInFlightRef.current) {
      return;
    }
    if (status === CAPTURE_STATUS.WAITING || status === CAPTURE_STATUS.SETTLING) {
      pollInFlightRef.current = true;
      try {
        await (status === CAPTURE_STATUS.WAITING ? waitForCapture() : settle());
      } catch (e) {
        logError(e);
        if (status === CAPTURE_STATUS.SETTLING && settleTicks.current >= SETTLE_MAX_TICKS) {
          stopPolling();
        }
      } finally {
        pollInFlightRef.current = false;
      }
      return;
    }
    if (finalizingRef.current) {
      return;
    }
    pollInFlightRef.current = true;
    // Ease off the pipeline as the wait grows.
    setPollDelay((prev) => Math.min(prev + POLL_BACKOFF_STEP_MS, POLL_MAX_INTERVAL_MS));
    try {
      const requestedId = emailId;
      const response = await getDocAssistantCaptureStatus(requestedId);
      if (finalizingRef.current || activeEmailRef.current !== requestedId) {
        return;
      }
      const { status: captureStatus, matches, relatedToId, relatedToApiName, attachmentMatches, files: fileResults } = response?.data || {};
      // A backend not yet deployed with `matches` only sends the legacy scalar pair; fall back to a
      // one-element list so a staggered rollout (new add-in, old backend) doesn't misread a real match.
      const relatedTos = matches || (relatedToId && relatedToApiName ? [{ relatedToId, relatedToApiName }] : []);
      // Per-file progress streams in every tick: finished files pick up their pipeline-assigned
      // names, unfinished ones render as skeletons in the Documents section.
      if (fileResults?.length) {
        trackedHashes.current = fileResults.filter((file) => file.matched).map((file) => file.hash).filter(Boolean);
        dispatch(applyDocAssistantFileResults(fileResults));
      }
      const deadlinePassed = Date.now() >= pollDeadline.current;
      if (captureStatus === BACKEND_SKIPPED) {
        // The capture ended with nothing to show (e.g. every attachment was too small): not a failure.
        finalizingRef.current = true;
        stopPolling();
        return;
      }
      if (captureStatus === 'complete') {
        // 'complete' means every file reached a terminal pipeline status. Rename runs after a file is
        // already terminal, so when anything matched wait for the new names (or a short grace budget)
        // before finishing, otherwise the rows keep their original names.
        const anyMatch = relatedTos.length || attachmentMatches?.length;
        const namesSettled = (fileResults || []).every((file) => !file.complete || file.fileName);
        if (!anyMatch || namesSettled || postCompleteTicks.current >= RENAME_GRACE_TICKS || deadlinePassed) {
          finalizingRef.current = true;
          if (relatedTos.length) {
            await finishWithMatters(relatedTos);
          } else {
            await finishWithoutMatter();
          }
          return;
        }
        postCompleteTicks.current += 1;
      } else if (deadlinePassed) {
        // Budget exhausted: assign whatever already matched rather than dropping it; surface failure
        // only when nothing did.
        finalizingRef.current = true;
        if (relatedTos.length) {
          await finishWithMatters(relatedTos);
        } else if (attachmentMatches?.length) {
          dispatch(clearDocAssistantProcessing());
          await finishWithoutMatter();
        } else {
          dispatch(clearDocAssistantProcessing());
          failCapture();
        }
        return;
      }
      if ((relatedTos.length || attachmentMatches?.length) && !matchedRef.current) {
        // The first match (the .eml's or an attachment's) navigates the pane to the documents view immediately.
        matchedRef.current = true;
        await showMatched();
      }
    } catch (e) {
      logError(e);
      if (Date.now() >= pollDeadline.current) {
        finalizingRef.current = true;
        if (matchedRef.current) {
          stopPolling();
        } else {
          dispatch(clearDocAssistantProcessing());
          failCapture();
        }
      }
    } finally {
      pollInFlightRef.current = false;
    }
  }, [CAPTURE_STATUS.WAITING, CAPTURE_STATUS.SEARCHING, CAPTURE_STATUS.MATCHED, CAPTURE_STATUS.SETTLING].includes(status) ? pollDelay : null);

  const startCapture = async () => {
    setBanner(null);
    beginSearching();
    try {
      await postDocAssistantCapture(emailId);
    } catch (e) {
      logError(e);
      failCapture();
    }
  };

  return { status, banner, startCapture, dismissBanner };
};

// An unknown arrival time (some hosts omit it) still waits rather than missing a capture.
const isRecentEmail = (createdAt) => {
  const createdMs = Date.parse(createdAt);
  return Number.isNaN(createdMs) || Date.now() - createdMs <= RECENT_EMAIL_MS;
};
